import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { queryClient, useTRPC } from '~/services/trpc'

import { toast } from '@hap/components'

export namespace IntentMutators {
  interface MutableValues {
    label?: string
    workspaceId?: string | null
  }

  export function useCreate({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.intents.create.mutationOptions({
        onMutate: async (values) => {
          const queryKey = trpc.intents.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousIntents = queryClient.getQueriesData({ queryKey })

          // Optimistically add the new intent
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            const newIntent = {
              id: '__TEMP__',
              label: values.label,
              workspaceId: values.workspaceId || null,
              isActive: true,
              suggested: null,
              createdAt: new Date(),
              updatedAt: new Date(),
            }
            return old ? [...old, newIntent] : [newIntent]
          })

          return { previousIntents }
        },
        onSuccess: () => {
          toast.success('Intent created')
          queryClient.invalidateQueries({
            queryKey: trpc.intents.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to create intent')
          // Rollback optimistic update
          if (context?.previousIntents) {
            context.previousIntents.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const create = useCallback(
      async (values: { label: string; workspaceId?: string }) => mutateAsync(values),
      [mutateAsync],
    )

    return { create, isPending }
  }

  export function useUpdate({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.intents.update.mutationOptions({
        onMutate: async (updates) => {
          const queryKey = trpc.intents.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousIntents = queryClient.getQueriesData({ queryKey })

          // Optimistically update the intent
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.map((intent) => {
              if (intent.id === updates.intentId) {
                return {
                  ...intent,
                  ...updates.values,
                  updatedAt: new Date(),
                }
              }
              return intent
            })
          })

          return { previousIntents }
        },
        onSuccess: () => {
          toast.success('Intent updated')
          queryClient.invalidateQueries({
            queryKey: trpc.intents.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to update intent')
          // Rollback optimistic update
          if (context?.previousIntents) {
            context.previousIntents.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const update = useCallback(
      async (values: { intentId: string; values: Partial<MutableValues> }) =>
        mutateAsync(values),
      [mutateAsync],
    )

    return { update, isPending }
  }

  export function useConfirm({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.intents.confirm.mutationOptions({
        onMutate: async (intentId) => {
          const queryKey = trpc.intents.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousIntents = queryClient.getQueriesData({ queryKey })

          // Optimistically confirm the intent
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.map((intent) => {
              if (intent.id === intentId) {
                return {
                  ...intent,
                  isActive: true,
                  updatedAt: new Date(),
                }
              }
              return intent
            })
          })

          return { previousIntents }
        },
        onSuccess: () => {
          toast.success('Intent confirmed')
          queryClient.invalidateQueries({
            queryKey: trpc.intents.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to confirm intent')
          // Rollback optimistic update
          if (context?.previousIntents) {
            context.previousIntents.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const confirm = useCallback(
      async (intentId: string) => mutateAsync(intentId),
      [mutateAsync],
    )

    return { confirm, isPending }
  }

  export function useDestroy({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.intents.destroy.mutationOptions({
        onMutate: async (intentId) => {
          const queryKey = trpc.intents.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousIntents = queryClient.getQueriesData({ queryKey })

          // Optimistically remove the intent
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.filter((intent) => intent.id !== intentId)
          })

          return { previousIntents }
        },
        onSuccess: () => {
          toast.success('Intent deleted')
          queryClient.invalidateQueries({
            queryKey: trpc.intents.list.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to delete intent')
          // Rollback optimistic update
          if (context?.previousIntents) {
            context.previousIntents.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const destroy = useCallback(
      async (intentId: string) => mutateAsync(intentId),
      [mutateAsync],
    )

    return { destroy, isPending }
  }
}
